<template>
  <div class="dashboard-sim-usage">
    <!-- Header -->
    <div class="su-header">
      <v-icon size="14" color="#0EA5E9" class="mr-1">mdi-sim</v-icon>
      <h3 class="su-title">{{ $t('org-dashboard.sim-usage-title') }}</h3>
    </div>

    <!-- Loading -->
    <div v-if="loading && !summary" class="su-loading">
      <v-progress-circular indeterminate size="24" width="2" color="#0EA5E9" />
    </div>

    <template v-else-if="summary">
      <!-- KPI cards -->
      <div class="su-kpi-row">
        <!-- Consommation période -->
        <div class="su-kpi-card">
          <div class="su-kpi-top">
            <span class="su-kpi-label">{{ $t('org-dashboard.sim-usage-total') }}</span>
            <v-icon size="18" color="#0EA5E9">mdi-database</v-icon>
          </div>
          <span class="su-kpi-value">{{ formattedTotalData }}</span>
          <span v-if="summary.avgPerSimMB != null" class="su-kpi-sub">{{ summary.avgPerSimMB }} MB / SIM</span>
        </div>

        <!-- Position cycle en cours (CTD) -->
        <div v-if="summary.ctdDataMB != null" class="su-kpi-card">
          <div class="su-kpi-top">
            <span class="su-kpi-label">{{ $t('org-dashboard.sim-usage-ctd') }}</span>
            <v-icon size="18" color="#6366F1">mdi-progress-clock</v-icon>
          </div>
          <span class="su-kpi-value">{{ formattedCtdData }}</span>
          <span v-if="summary.ctdAvgPerSimMB != null" class="su-kpi-sub">{{ summary.ctdAvgPerSimMB }} MB / SIM</span>
        </div>

        <!-- Connectées -->
        <div class="su-kpi-card su-kpi--clickable" @click="$emit('sim-drilldown', { type: 'connected' })">
          <div class="su-kpi-top">
            <span class="su-kpi-label">{{ $t('org-dashboard.sim-usage-connected') }}</span>
            <v-icon size="18" color="#22c55e">mdi-sim</v-icon>
          </div>
          <span class="su-kpi-value">{{ activeSimCount }} <span class="su-kpi-unit">/ {{ totalSimCount }}</span></span>
        </div>

        <!-- Sessions -->
        <div v-if="summary.totalSessionCount != null" class="su-kpi-card">
          <div class="su-kpi-top">
            <span class="su-kpi-label">{{ $t('org-dashboard.sim-usage-sessions') }}</span>
            <v-icon size="18" color="#f59e0b">mdi-swap-vertical</v-icon>
          </div>
          <span class="su-kpi-value">{{ formattedSessionCount }}</span>
        </div>

        <!-- Déconnectées -->
        <div v-if="simDisconnected > 0" class="su-kpi-card su-kpi--clickable" @click="$emit('sim-drilldown', { type: 'disconnected' })">
          <div class="su-kpi-top">
            <span class="su-kpi-label">{{ $t('org-dashboard.sim-usage-disconnected') }}</span>
            <v-icon size="18" color="#f59e0b">mdi-sim-alert</v-icon>
          </div>
          <span class="su-kpi-value su-kpi-value--orange">{{ simDisconnected }}</span>
        </div>

        <!-- Hors Tele2 -->
        <div v-if="simNotInTele2 > 0" class="su-kpi-card su-kpi--clickable" @click="$emit('sim-drilldown', { type: 'notInTele2' })">
          <div class="su-kpi-top">
            <span class="su-kpi-label">{{ $t('org-dashboard.sim-usage-not-in-tele2') }}</span>
            <v-icon size="18" color="#ef4444">mdi-sim-off</v-icon>
          </div>
          <span class="su-kpi-value su-kpi-value--warn">{{ simNotInTele2 }}</span>
        </div>

        <!-- Sans SIM -->
        <div v-if="simNoSim > 0" class="su-kpi-card su-kpi--clickable" @click="$emit('sim-drilldown', { type: 'noSim' })">
          <div class="su-kpi-top">
            <span class="su-kpi-label">{{ $t('org-dashboard.sim-none') }}</span>
            <v-icon size="18" color="#9ca3af">mdi-cancel</v-icon>
          </div>
          <span class="su-kpi-value">{{ simNoSim }}</span>
        </div>
      </div>

      <!-- Charts + Top consumers (only if data) -->
      <div v-if="topConsumers.length" class="su-body-row">
        <!-- Left: Trend + Distribution stacked -->
        <div class="su-body-left">
          <div class="su-chart-block">
            <h4 class="su-section-title">{{ $t('org-dashboard.sim-usage-trend') }}</h4>
            <div v-if="usageTrend.length" class="su-chart">
              <highcharts :options="trendChartOptions" />
            </div>
            <div v-else class="su-chart-empty">
              <v-icon color="#d1d5db" size="24">mdi-chart-line</v-icon>
              <span>{{ $t('org-dashboard.health-no-data') }}</span>
            </div>
          </div>
          <div class="su-chart-block">
            <h4 class="su-section-title">{{ $t('org-dashboard.sim-usage-distribution') }}</h4>
            <div v-if="distribution.length" class="su-chart">
              <highcharts :options="distributionChartOptions" />
            </div>
            <div v-else class="su-chart-empty">
              <v-icon color="#d1d5db" size="24">mdi-chart-bar</v-icon>
              <span>{{ $t('org-dashboard.health-no-data') }}</span>
            </div>
          </div>
        </div>

        <!-- Right: Top consumers -->
        <div class="su-body-right">
          <div class="su-top-header">
            <h4 class="su-section-title">{{ $t('org-dashboard.sim-usage-top-consumers') }}</h4>
            <span class="su-top-count">{{ topConsumers.length }}</span>
          </div>
          <div class="su-top-list">
            <div
              v-for="(item, idx) in topConsumers"
              :key="item.iccid"
              class="su-top-item"
              @click="$emit('charger-click', item.chargingPointId)"
            >
              <span class="su-top-rank" :class="rankClass(idx)">{{ idx + 1 }}</span>
              <div class="su-top-info">
                <span class="su-top-id">{{ item.identifier }}</span>
                <span class="su-top-iccid">{{ item.iccid }}</span>
              </div>
              <div v-if="item.sessionCount != null" class="su-top-sessions">
                <span class="su-top-sessions-value">{{ item.sessionCount }}</span>
                <span class="su-top-sessions-label">{{ $t('org-dashboard.sim-usage-sessions') }}</span>
              </div>
              <div class="su-top-data">
                <span class="su-top-data-value">{{ item.dataMB }} MB</span>
                <span v-if="item.ctdDataMB != null" class="su-top-data-ctd">{{ $t('org-dashboard.sim-usage-ctd-short') }} {{ item.ctdDataMB }} MB</span>
                <div class="su-top-bar-wrap">
                  <div class="su-top-bar" :style="{ width: dataBarWidth(item.dataMB) }"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>

    <!-- Empty state -->
    <div v-else class="su-empty">
      <v-icon color="#d1d5db" size="32">mdi-sim-off</v-icon>
      <span>{{ $t('org-dashboard.health-no-data') }}</span>
    </div>
  </div>
</template>

<script>
import { Chart } from 'highcharts-vue'

export default {
  name: 'DashboardSimUsage',
  components: { highcharts: Chart },

  props: {
    simStatus: { type: Object, default: null },
    summary: { type: Object, default: null },
    topConsumers: { type: Array, default: () => [] },
    usageTrend: { type: Array, default: () => [] },
    distribution: { type: Array, default: () => [] },
    loading: { type: Boolean, default: false },
  },

  computed: {
    simConnected() {
      return (this.simStatus && this.simStatus.connected) || 0
    },
    simDisconnected() {
      return (this.simStatus && this.simStatus.disconnected) || 0
    },
    simNotInTele2() {
      return (this.simStatus && this.simStatus.notInTele2) || 0
    },
    simNoSim() {
      return (this.simStatus && this.simStatus.noSim) || 0
    },
    simWithSim() {
      return (this.simStatus && this.simStatus.withSim) || 0
    },

    activeSimCount() {
      if (this.summary && this.summary.activeSims != null) return this.summary.activeSims
      return this.simConnected
    },
    totalSimCount() {
      if (this.summary && this.summary.totalSims != null) return this.summary.totalSims
      return this.simWithSim
    },

    formattedTotalData() {
      if (!this.summary) return '–'
      const mb = this.summary.totalDataMB
      if (mb >= 1000) return (mb / 1024).toFixed(1) + ' GB'
      return mb + ' MB'
    },

    formattedCtdData() {
      if (!this.summary || this.summary.ctdDataMB == null) return '–'
      const mb = this.summary.ctdDataMB
      if (mb >= 1000) return (mb / 1024).toFixed(1) + ' GB'
      return mb + ' MB'
    },

    formattedSessionCount() {
      const count = this.summary && this.summary.totalSessionCount
      if (count == null) return '–'
      if (count >= 1000) return (count / 1000).toFixed(1) + 'k'
      return count
    },

    maxDataMB() {
      if (!this.topConsumers.length) return 1
      return Math.max(...this.topConsumers.map(c => c.dataMB))
    },

    trendChartOptions() {
      const data = this.usageTrend.map(d => [new Date(d.date).getTime(), d.dataMB])
      return {
        chart: { type: 'areaspline', height: 220, backgroundColor: 'transparent', spacing: [8, 0, 8, 0] },
        title: { text: '' },
        credits: { enabled: false },
        legend: { enabled: false },
        xAxis: {
          type: 'datetime',
          labels: { style: { fontSize: '10px', color: '#9ca3af' } },
          lineColor: '#e5e7eb',
          tickColor: '#e5e7eb',
        },
        yAxis: {
          title: { text: '' },
          labels: { format: '{value} MB', style: { fontSize: '10px', color: '#9ca3af' } },
          gridLineColor: '#f3f4f6',
          min: 0,
        },
        tooltip: {
          shared: true,
          backgroundColor: 'white',
          borderColor: '#e5e7eb',
          borderRadius: 8,
          shadow: { color: 'rgba(0,0,0,0.06)', offsetX: 0, offsetY: 2, width: 8 },
          style: { fontSize: '11px' },
          valueSuffix: ' MB',
        },
        series: [{
          name: 'Data',
          data,
          color: '#0EA5E9',
          fillColor: {
            linearGradient: { x1: 0, y1: 0, x2: 0, y2: 1 },
            stops: [[0, 'rgba(14,165,233,0.2)'], [1, 'rgba(14,165,233,0.02)']],
          },
          marker: { radius: 2 },
        }],
      }
    },

    distributionChartOptions() {
      const categories = this.distribution.map(d => d.bucket)
      const data = this.distribution.map(d => d.count)
      return {
        chart: { type: 'column', height: 220, backgroundColor: 'transparent', spacing: [8, 0, 8, 0] },
        title: { text: '' },
        credits: { enabled: false },
        legend: { enabled: false },
        xAxis: {
          categories,
          labels: { style: { fontSize: '10px', color: '#9ca3af' } },
          lineColor: '#e5e7eb',
        },
        yAxis: {
          title: { text: '' },
          labels: { style: { fontSize: '10px', color: '#9ca3af' } },
          gridLineColor: '#f3f4f6',
          min: 0,
          allowDecimals: false,
        },
        tooltip: {
          backgroundColor: 'white',
          borderColor: '#e5e7eb',
          borderRadius: 8,
          shadow: { color: 'rgba(0,0,0,0.06)', offsetX: 0, offsetY: 2, width: 8 },
          style: { fontSize: '11px' },
        },
        plotOptions: {
          column: {
            borderRadius: 4,
            color: '#6366F1',
            cursor: 'pointer',
            point: {
              events: {
                click: (e) => {
                  this.$emit('sim-drilldown', { type: 'distribution', bucket: e.point.category })
                },
              },
            },
          },
        },
        series: [{
          name: 'SIMs',
          data,
        }],
      }
    },
  },

  methods: {
    rankClass(idx) {
      if (idx === 0) return 'su-top-rank--gold'
      if (idx === 1) return 'su-top-rank--silver'
      if (idx === 2) return 'su-top-rank--bronze'
      return ''
    },

    dataBarWidth(dataMB) {
      if (!this.maxDataMB) return '0%'
      return Math.round(dataMB / this.maxDataMB * 100) + '%'
    },
  },
}
</script>

<style lang="scss" scoped>
.dashboard-sim-usage {
  background: white;
  border-radius: 12px;
  padding: 20px;
  border: 1px solid #e5e7eb;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05);
}

.su-header {
  display: flex;
  align-items: center;
  border-bottom: 1px solid #f3f4f6;
  padding-bottom: 12px;
  margin-bottom: 16px;
}

.su-title {
  font-size: 14px;
  font-weight: 700;
  color: #1f2937;
}

.su-loading {
  display: flex;
  justify-content: center;
  padding: 40px 0;
}

/* ── KPI row ── */
.su-kpi-row {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 10px;
  margin-bottom: 16px;

  @media (min-width: 768px) {
    grid-template-columns: repeat(3, 1fr);
  }

  @media (min-width: 1264px) {
    grid-template-columns: repeat(auto-fit, minmax(0, 1fr));
  }
}

.su-kpi-card {
  background: white;
  border-radius: 8px;
  padding: 14px;
  border: 1px solid #e5e7eb;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
  transition: box-shadow 0.2s;

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  }
}

.su-kpi--clickable {
  cursor: pointer;
  transition: box-shadow 0.2s, border-color 0.2s, background 0.2s;

  &:hover {
    box-shadow: 0 4px 12px rgba(14, 165, 233, 0.15);
    border-color: #bae6fd;
    background: #f0f9ff;
  }
}

.su-kpi-top {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 8px;
}

.su-kpi-label {
  font-size: 10px;
  font-weight: 600;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.su-kpi-value {
  font-size: 22px;
  font-weight: 700;
  color: #111827;
  line-height: 1.2;

  &--warn {
    color: #ef4444;
  }

  &--orange {
    color: #d97706;
  }
}

.su-kpi-unit {
  font-size: 13px;
  font-weight: 400;
  color: #6b7280;
}

.su-kpi-sub {
  font-size: 10px;
  font-weight: 500;
  color: #9ca3af;
  margin-top: 2px;
}

/* ── Body: Charts left + Top right ── */
.su-body-row {
  display: grid;
  grid-template-columns: 1fr;
  gap: 16px;
  border-top: 1px solid #f3f4f6;
  padding-top: 16px;

  @media (min-width: 960px) {
    grid-template-columns: 1fr 1fr;
  }
}

.su-body-left {
  display: flex;
  flex-direction: column;
  gap: 16px;
  min-width: 0;
}

.su-body-right {
  min-width: 0;
}

.su-section-title {
  font-size: 12px;
  font-weight: 700;
  color: #374151;
  margin-bottom: 8px;
}

.su-chart {
  border: 1px solid #f3f4f6;
  border-radius: 8px;
  padding: 8px;
  background: #fafafa;
}

.su-chart-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  padding: 32px 0;

  span {
    font-size: 11px;
    color: #9ca3af;
  }
}

/* ── Top consumers ── */

.su-top-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
}

.su-top-count {
  font-size: 10px;
  font-weight: 700;
  color: #9ca3af;
  background: #f3f4f6;
  border-radius: 10px;
  padding: 2px 8px;
}

.su-top-list {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.su-top-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 12px;
  border-radius: 8px;
  border: 1px solid #f3f4f6;
  background: #fafafa;
  cursor: pointer;
  transition: all 0.15s;

  &:hover {
    background: #f0f9ff;
    border-color: #bae6fd;
  }
}

.su-top-rank {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 6px;
  font-size: 11px;
  font-weight: 700;
  color: #9ca3af;
  background: #f3f4f6;
  flex-shrink: 0;

  &--gold {
    color: #b45309;
    background: #fef3c7;
  }

  &--silver {
    color: #4b5563;
    background: #e5e7eb;
  }

  &--bronze {
    color: #92400e;
    background: #fed7aa;
  }
}

.su-top-info {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 1px;
}

.su-top-id {
  font-size: 12px;
  font-weight: 600;
  color: #1976d2;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.su-top-iccid {
  font-size: 10px;
  font-family: monospace;
  color: #9ca3af;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.su-top-sessions {
  display: flex;
  flex-direction: column;
  align-items: center;
  flex-shrink: 0;
  padding: 0 8px;
}

.su-top-sessions-value {
  font-size: 13px;
  font-weight: 700;
  color: #374151;
}

.su-top-sessions-label {
  font-size: 9px;
  color: #9ca3af;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}

.su-top-data {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  flex-shrink: 0;
  min-width: 90px;
}

.su-top-data-value {
  font-size: 12px;
  font-weight: 700;
  color: #1f2937;
  white-space: nowrap;
}

.su-top-data-ctd {
  font-size: 9px;
  color: #9ca3af;
  white-space: nowrap;
}

.su-top-bar-wrap {
  width: 100%;
  height: 4px;
  border-radius: 2px;
  background: #f3f4f6;
  margin-top: 4px;
}

.su-top-bar {
  height: 100%;
  border-radius: 2px;
  background: linear-gradient(90deg, #0EA5E9, #6366F1);
  transition: width 0.4s ease;
}

.su-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 40px 0;

  span {
    font-size: 12px;
    color: #9ca3af;
  }
}
</style>
